import { useLocation } from '@reach/router';
import classNames from 'classnames';
import React, { use, useLayoutEffect, useRef, useState } from 'react';

import { REDESIGN_BREADCRUMBS_HEIGHT_CSS_VARIABLE } from 'src/components/common/breadcrumbs/breadcrumbs-helper';
import { WebsiteLink } from 'src/components/common/website-link';
import { HeaderNavigation } from 'src/components/layout/redesign/header-desktop/header-navigation';
import { MENU_HIDE_DURATION_MS } from 'src/components/layout/redesign/header-desktop/heder-desktop-helper';
import type { HeaderButtonProps } from 'src/components/layout/redesign/header-mobile/header-button';
import type { BaseHeaderContent } from 'src/components/layout/redesign/header-mobile/header-menu';
import { HeaderLogo } from 'src/components/layout/redesign/header-shared/header-logo';
import {
  HEADER_ADDITIONAL_TOP_PADDING_PX,
  REDESIGN_HEADER_HEIGHT_CSS_VARIABLE,
} from 'src/components/layout/redesign/header-shared/header-styling';
import { SiteContext } from 'src/context/site-context';
import useHeldState from 'src/custom-hooks/use-held-state';
import {
  amplitudeBrandName,
  amplitudeLogoClickEvent,
  amplitudeTrackEvent,
} from 'src/helpers/amplitude-event-track';
import { isOnClubsPage } from 'src/helpers/path-matching-helper';
import { STACKED_STICKY_ITEM_HEIGHT_REDUCTION } from 'src/styling/constants';
import { matchReducedMotion } from 'src/styling/media-queries';

import throttleToAnimationFrames from '../../../../helpers/throttle-to-animation-frames';

import { HeaderActions } from './header-action';
import * as styles from './index.module.scss';

type HeaderDesktopProps = {
  headerContent: BaseHeaderContent | null;
  ribbonChildren?: React.ReactNode;
  primaryButton?: HeaderButtonProps;
  secondaryButton?: HeaderButtonProps;
  onMenuChanged?: () => void;
  additionalSubMenus?: React.ReactNode;
  forceDesktopHeader?: boolean;
};

const HeaderDesktop = ({
  headerContent,
  ribbonChildren,
  primaryButton,
  secondaryButton,
  onMenuChanged,
  additionalSubMenus,
  forceDesktopHeader = false,
}: HeaderDesktopProps) => {
  const { pageLevel, isGlobalPage, buildType, language, isHarbour } = use(SiteContext);
  const location = useLocation();
  const isClubsPage = isOnClubsPage(location);
  const [displayedSubMenuIndex, setDisplayedSubMenuIndex, holdSubMenuIndexChangesUntilAfterMs] =
    useHeldState<number | null>(null);
  const [isUserTabbing, setIsUserTabbing] = useState<boolean>(false);
  const [canOpenSubMenu, setCanOpenSubMenu] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const onFinishMenuAction = () => {
    onMenuChanged?.();
    closeAllSubMenus();
  };

  const closeAllSubMenus = () => {
    setDisplayedSubMenuIndex(null);
    holdSubMenuIndexChangesUntilAfterMs(MENU_HIDE_DURATION_MS); // Wait for the menu to fully close before re-enabling user interaction. Might also help UX when reduced motion is enabled.
  };

  const openSubMenu = (index: number) => {
    onMenuChanged?.();
    setDisplayedSubMenuIndex(index);
  };

  useLayoutEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) {
      return;
    }

    document.documentElement.style.setProperty(
      'scroll-padding-top',
      `calc(var(--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE}) + var(--${REDESIGN_BREADCRUMBS_HEIGHT_CSS_VARIABLE}, 0px))`
    );

    const updateHeaderHeightVariable = () => {
      const containerHeight = containerElement.offsetHeight;
      if (!containerHeight) {
        return;
      }

      const nextValue = `${containerHeight - HEADER_ADDITIONAL_TOP_PADDING_PX - STACKED_STICKY_ITEM_HEIGHT_REDUCTION}px`;
      const docStyle = document.documentElement.style;
      if (docStyle.getPropertyValue(`--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE}`) === nextValue) {
        return;
      }

      docStyle.setProperty(`--${REDESIGN_HEADER_HEIGHT_CSS_VARIABLE}`, nextValue);
    };

    const { throttled: onResize, cancel: cancelPendingResize } = throttleToAnimationFrames(
      updateHeaderHeightVariable
    );
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(containerElement);
    updateHeaderHeightVariable();

    return () => {
      document.documentElement.style.removeProperty('scroll-padding-top');
      resizeObserver.disconnect();
      cancelPendingResize();
    };
  }, []);

  if (!headerContent) {
    return null;
  }

  const logoAmplitudeAction = () => {
    void amplitudeTrackEvent(amplitudeLogoClickEvent, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
      brand: amplitudeBrandName(buildType),
      buildType,
      language,
      ctaName: 'logo',
      component: 'Header',
    });
  };

  const logoClick = () => {
    onFinishMenuAction();
    const prefersReducedMotion = matchReducedMotion().matches;
    document.body.scrollIntoView({ behavior: prefersReducedMotion ? 'instant' : 'smooth' });
    logoAmplitudeAction();
  };

  return (
    <header
      ref={containerRef}
      className={classNames(
        styles.container,
        (isClubsPage || isHarbour) && styles.containerShadow,
        forceDesktopHeader && styles.containerForceDesktop
      )}
      onPointerLeave={closeAllSubMenus}
      onPointerOver={() => setCanOpenSubMenu(true)}
    >
      <div onPointerOver={closeAllSubMenus} className={styles.ribbonWrapper}>
        {ribbonChildren}
      </div>
      <div className={styles.header}>
        {isGlobalPage && (
          <div className={styles.logo} onPointerOver={closeAllSubMenus}>
            <WebsiteLink to="/" goToGlobal handleClick={logoClick}>
              <HeaderLogo
                davidLloydSize="12.5rem"
                meridianSize="12.75rem"
                harbourSize="12.75rem"
                togetherFoundationSize="10rem"
              />
            </WebsiteLink>
          </div>
        )}
        <HeaderNavigation
          onFinishMenuAction={onFinishMenuAction}
          onHover={closeAllSubMenus}
          logoClick={logoClick}
          isUserTabbing={isUserTabbing}
          headerContent={headerContent}
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
          openSubMenu={openSubMenu}
          displayedSubMenuIndex={displayedSubMenuIndex}
          setIsUserTabbing={setIsUserTabbing}
          setCanOpenSubMenu={setCanOpenSubMenu}
          canOpenSubMenu={canOpenSubMenu}
        />
        {isGlobalPage && (
          <div className={styles.endActionsWrapper}>
            <HeaderActions
              primaryButton={primaryButton}
              secondaryButton={secondaryButton}
              onHover={closeAllSubMenus}
              linkDropdownCta={headerContent.linkDropdownCta}
            />
          </div>
        )}
        {additionalSubMenus}
      </div>
    </header>
  );
};

export default HeaderDesktop;
